import { useQuery } from "@tanstack/react-query";
import { Link, useMatchRoute } from "@tanstack/react-router";
import {
	CreditCard,
	FileText,
	LayoutGrid,
	Palette,
	ShieldCheck,
	Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { ClerkGate, ClerkHeaderControls } from "../auth/clerk-lazy.js";
import { TeamSwitcher } from "../features/team/TeamSwitcher.js";
import { Footer } from "../components/Footer.js";
import { AdminModeBanner } from "../components/AdminModeBanner.js";
import { ImpersonationBanner } from "../components/ImpersonationBanner.js";
import { MakerDocLogo, MakerDocMark } from "../components/MakerDocLogo.js";
import { PrimaryNav } from "../components/PrimaryNav.js";
import { MakerXLogo } from "../components/MakerXLogo.js";
import {
	NotificationBell,
	NotificationToaster,
} from "../components/NotificationBell.js";
import { SignOutButton } from "../components/SignOutButton.js";
import { ToastProvider } from "../components/Toast.js";
import { UsageChip } from "../components/UsageChip.js";
import { CookieConsentBanner } from "../features/cookie-consent/CookieConsentBanner.js";
import { useAnalytics } from "../lib/analytics.js";
import { ApiError, api } from "../lib/api.js";
import { isClerkMode, useConfig } from "../lib/config.js";

export function RootLayout({ children }: { children: ReactNode }) {
	const config = useConfig();
	const isClerk = isClerkMode(config);
	// Load GA4 + record pageviews (live site only, consent-gated, never on
	// /admin). Mounted here so it covers every route, incl. the signed-out
	// landing.
	useAnalytics();
	// The doc editor is a full-height, single-bar workspace: it renders its own
	// top bar (back · title · toolbar · account), so the app-shell header is
	// suppressed there to avoid two stacked bars. This is a route check, not a
	// breakpoint — robust at every width.
	const matchRoute = useMatchRoute();
	const onDocRoute =
		!!matchRoute({ to: "/docs/$docId" }) ||
		// The presenter view is a bare window: slides, notes, clock — no app
		// chrome at all.
		!!matchRoute({ to: "/docs/$docId/presenter" });
	// First-run onboarding is a focused journey: keep the header (logo + account
	// for an escape hatch) but drop the nav links/usage/switcher so nothing
	// competes with the one task. Also avoids the nav overflowing on mobile.
	const onOnboardingRoute = !!matchRoute({ to: "/onboarding" });
	// The public contact page renders its own minimal logo header, so suppress the
	// app-shell header there too (otherwise two stacked navbars).
	const onContactRoute = !!matchRoute({ to: "/contact" });
	// The legal pages (privacy/terms) render their own LegalLayout chrome
	// (minimal header + footer), so suppress the app-shell header and footer
	// there to avoid doubling up.
	const onLegalRoute =
		!!matchRoute({ to: "/privacy" }) || !!matchRoute({ to: "/terms" });
	const me = useQuery({
		queryKey: ["me"],
		queryFn: () => api.me(),
		retry: false,
	});

	const signedIn =
		!!me.data && !(me.error instanceof ApiError && me.error.status === 401);
	// Admin Mode: the active team is an unclaimed preloaded brand. It has no
	// customer and no billing, so the billing chrome is left out while in it.
	const inAdminMode = me.data?.activeOrg?.kind === "preload";

	const shell = (
		<div className="app-ambient min-h-full flex flex-col relative">
			{signedIn && me.data?.impersonator && (
				<ImpersonationBanner
					viewingAs={me.data.name || me.data.email}
					adminEmail={me.data.impersonator.email}
				/>
			)}
			{signedIn && inAdminMode && !me.data?.impersonator && (
				<AdminModeBanner
					brandName={me.data?.activeOrg?.name ?? ""}
					domain={me.data?.activeOrg?.preloadDomain ?? null}
				/>
			)}
			{/* The single always-on identity element — slim accent strip top-left.
          Spec: 4px tall, 14% wide. (The editor renders its own, so skip it
          there.) */}
			{!onDocRoute && (
				<span
					aria-hidden="true"
					className="absolute top-0 left-0 h-[3px] w-[14%] bg-[var(--color-mx-electric)] z-10"
				/>
			)}
			{!onDocRoute && !onContactRoute && !onLegalRoute && (
				<header className="border-b mx-divider px-4 sm:px-6 py-3.5 flex items-center justify-between gap-2 bg-black/35 backdrop-blur-md relative z-20">
					{/* Left cluster: brand + team switcher — pinned top-left so
					    "which team am I in" is the first thing you see; selecting
					    a team re-scopes the app. */}
					<div
						className="flex items-center gap-2 sm:gap-4 min-w-0"
						data-testid="app-header-left"
					>
						<Link
							to="/"
							data-testid="app-home-link"
							className="group flex items-center gap-3 -my-1 py-1 pr-1 shrink-0"
							aria-label={isClerk ? "makerdoc.ai home" : "MakerX Multiplayer home"}
						>
							{isClerk ? (
								<>
									{/* The wordmark is the first thing to give up its width: on
									    a phone the team you're in and where you can go both
									    matter more than saying the product's name twice.
									    Hidden by a WRAPPER, not by a class passed into the
									    lockup — that merges with the component's own
									    `inline-flex` and the display utility that wins is
									    whichever Tailwind emitted last, which was not `hidden`. */}
									<span className="hidden sm:inline-flex">
										<MakerDocLogo
											markClassName="h-6 w-auto"
											className="transition-opacity duration-200 group-hover:opacity-90"
										/>
									</span>
									<span className="sm:hidden">
										<MakerDocMark className="h-6 w-auto" />
									</span>
								</>
							) : (
								<>
									<MakerXLogo className="h-6 w-auto transition-opacity duration-200 group-hover:opacity-90" />
									<span className="mx-eyebrow hidden sm:inline transition-colors duration-200 group-hover:text-white/70">
										Multiplayer
									</span>
								</>
							)}
						</Link>
						{/* Team switcher — b2c only, hidden on the focused onboarding journey. */}
						{signedIn && isClerk && !onOnboardingRoute && (
							<>
								<span
									aria-hidden="true"
									className="hidden lg:block h-6 w-px bg-[var(--color-mx-line-strong)]"
								/>
								<TeamSwitcher
									impersonating={!!me.data?.impersonator}
									teamName={me.data?.activeOrg?.name}
								/>
							</>
						)}
					</div>
					{/* `shrink-0`: this cluster is all fixed-size controls, so when the
					    header runs out of room the LEFT side must truncate. Without it
					    both sides shrink below their contents and paint over each
					    other — see tests/app-header-fit.b2c.spec.ts. */}
					<div
						className="flex items-center gap-1.5 sm:gap-3 shrink-0"
						data-testid="app-header-right"
					>
						{signedIn && onOnboardingRoute && isClerk && (
							<ClerkHeaderControls />
						)}
						{signedIn && !onOnboardingRoute && (
							<>
								{/* Primary navigation — the places you can go. A segmented
                  control from `lg` up, one menu button below it; see
                  PrimaryNav for why that threshold and not `md`. */}
								<PrimaryNav
									foldBelow={isClerk ? "lg" : "sm"}
									items={[
										{
											to: "/docs",
											label: "Docs",
											icon: <FileText className={NAV_ICON} aria-hidden />,
										},
										...(isClerk
											? ([
													{
														to: "/brand",
														label: "Brand",
														icon: <Palette className={NAV_ICON} aria-hidden />,
													},
													{
														to: "/team",
														label: "Team",
														icon: <Users className={NAV_ICON} aria-hidden />,
													},
												] as const)
											: ([
													// Templates-only editions get the same destination
													// under the name of the one thing it holds — there
													// is no brand profile behind it to call "Brand".
													{
														to: "/brand/templates",
														label: "Templates",
														icon: (
															<LayoutGrid className={NAV_ICON} aria-hidden />
														),
														testId: "templates-nav-link",
													},
												] as const)),
										...(isClerk && config.stripeEnabled && !inAdminMode
											? ([
													{
														to: "/billing",
														label: "Billing",
														icon: (
															<CreditCard className={NAV_ICON} aria-hidden />
														),
													},
												] as const)
											: []),
										...(me.data?.isAdmin
											? ([
													{
														to: "/admin",
														label: "Admin",
														icon: (
															<ShieldCheck className={NAV_ICON} aria-hidden />
														),
														testId: "admin-nav-link",
													},
												] as const)
											: []),
									]}
								/>

								{/* Divider between "where can I go" and "my account / status". */}
								<span
									aria-hidden="true"
									className="hidden lg:block h-6 w-px bg-[var(--color-mx-line-strong)]"
								/>

								{/* Utility cluster — status + account, not navigation. */}
								<div className="flex items-center gap-2 sm:gap-3">
									{/* Status, not navigation — and the same figure is on
									    /billing, so it yields first on a phone. */}
									{isClerk && config.stripeEnabled && !inAdminMode && (
										<span className="hidden sm:inline-flex">
											<UsageChip />
										</span>
									)}
									<NotificationBell />
									{isClerk ? (
										// Account control only (profile + sign-out). The team
										// switcher now lives top-left; the bridge tears down
										// the mx_session on Clerk sign-out.
										<ClerkHeaderControls />
									) : (
										<>
											<span className="mx-mono-meta hidden md:inline">
												{me.data?.email}
											</span>
											<SignOutButton variant="text" />
										</>
									)}
								</div>
							</>
						)}
					</div>
				</header>
			)}
			<main className="flex-1 relative">{children}</main>
			{/* Global footer (legal links + EU cookie-preferences). Suppressed on
          the full-screen editor, the focused onboarding journey, and the
          pages that render their own chrome (contact, legal). */}
			{!onDocRoute &&
				!onOnboardingRoute &&
				!onContactRoute &&
				!onLegalRoute && <Footer />}
			{signedIn && <NotificationToaster />}
			{/* EU-only cookie-consent banner; self-gates (renders null for
          non-EU visitors and once a choice is recorded). */}
			<CookieConsentBanner />
		</div>
	);

	// b2c: gate the whole app behind the auth state machine (sign-in /
	// minting / error), so no page renders without a session and a failed
	// exchange shows a retry instead of a dead-end. Non-clerk editions
	// render the shell directly (sync phase defaults to "ready").
	// ToastProvider wraps everything so any surface can raise the app-wide
	// top-right toast stack.
	return (
		<ToastProvider>
			{isClerk ? <ClerkGate>{shell}</ClerkGate> : shell}
		</ToastProvider>
	);
}

const NAV_ICON = "h-4 w-4 shrink-0";
